import React, { useEffect, useRef, useState } from 'react';
import { Fade, Grow, Typography } from '@mui/material';
import Slider from 'react-slick';
import ArrowButton from '../../atoms/arrow-button/arrowButton';
import Button2 from '../../atoms/button2/button2';
import RichtextDescription from '../../atoms/richtext-description/richtextDescription';
import useScreenSize from '../../../hooks/useScreenSize';
import * as styles from './steps.module.scss';
import './steps.scss';

export interface IStepsProps {
    subtitle: string;
    title: string;
    description: any;
    steps: [
        {
            label: string;
            body: any;
            btnLabel: string;
            redirect: string;
        },
    ];
}

const Steps = ({ subtitle, title, description, steps }: IStepsProps) => {
    const [selected, setSelected] = useState(0);
    const [checked, setChecked] = useState(true);
    const [isVisible, setIsVisible] = useState(false);
    const StepsRef = useRef(null);
    const screenSize = useScreenSize();

    const settings = {
        dots: false,
        infinite: false,
        speed: 500,
        slidesToShow: 1,
        slidesToScroll: 1,
        nextArrow: (
            <ArrowButton
                extraFunction={() =>
                    setSelected(
                        selected === steps.length - 1
                            ? steps.length - 1
                            : selected + 1,
                    )
                }
            />
        ),
        prevArrow: (
            <ArrowButton
                extraFunction={() =>
                    setSelected(selected === 0 ? 0 : selected - 1)
                }
            />
        ),
    };

    useEffect(() => {
        const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) {
                setIsVisible(entries[0].isIntersecting);
            }
        });
        if (StepsRef.current) {
            observer.observe(StepsRef.current);
        }
        return () => observer.disconnect();
    }, []);

    const handleBtnClick = (index) => {
        setChecked((prev) => !prev);
        setTimeout(() => {
            setSelected(index);
        }, 200);
        setTimeout(() => {
            setChecked((prev) => !prev);
        }, 205);
    };

    return (
        <Fade in={isVisible} timeout={1000}>
            <section ref={StepsRef} className={styles.o_steps}>
                <>
                    {subtitle && (
                        <Typography variant="body1" className="a-home__desc">
                            {subtitle}
                        </Typography>
                    )}
                    {title && (
                        <Typography component="h2" className="a-home__h2">
                            {title}
                        </Typography>
                    )}
                    {description && (
                        <div className={styles.o_steps__desc}>
                            {description}
                        </div>
                    )}
                </>
                <Fade in={checked} timeout={500}>
                    <div className={styles.o_steps__container}>
                        {screenSize > 1060 ? (
                            <div className={styles.o_steps__buttons}>
                                {steps.map((step, index) => (
                                    <button
                                        key={index}
                                        onClick={() => handleBtnClick(index)}
                                    >
                                        <span
                                            className={`${
                                                styles.o_steps__number
                                            } ${
                                                index === selected
                                                    ? styles.o_steps__selectednum
                                                    : ''
                                            }`}
                                        >
                                            {index + 1}
                                        </span>
                                        <h3
                                            className={`${
                                                styles.o_steps__label
                                            } ${
                                                index === selected
                                                    ? styles.o_steps__selectedlabel
                                                    : ''
                                            }`}
                                        >
                                            {step.label}
                                        </h3>
                                    </button>
                                ))}
                            </div>
                        ) : (
                            <div
                                className={`${styles.o_steps__mobile} o-steps__mobile`}
                            >
                                <div className={styles.o_steps__dots}>
                                    {steps.map((_, index) => (
                                        <span
                                            key={index}
                                            className={`${
                                                styles.o_steps__dot
                                            } ${
                                                index === selected
                                                    ? styles.o_steps__currentdot
                                                    : ''
                                            } ${
                                                index < selected
                                                    ? styles.o_steps__pastdot
                                                    : ''
                                            }`}
                                        />
                                    ))}
                                </div>
                                <Slider
                                    className={styles.o_steps__tabs}
                                    {...settings}
                                >
                                    {steps.map((step, index) => (
                                        <Typography
                                            key={index}
                                            component="h3"
                                            className={styles.o_steps__tab}
                                        >
                                            {step.label}
                                        </Typography>
                                    ))}
                                </Slider>
                            </div>
                        )}
                        <Grow
                            in={checked}
                            {...(checked ? { timeout: 500 } : {})}
                        >
                            <div
                                className={styles.o_steps__content}
                                id="o_steps__content--effect"
                            >
                                <RichtextDescription
                                    descripcion={steps[selected].body}
                                />
                                {steps[selected].btnLabel &&
                                    steps[selected].redirect && (
                                        <Button2
                                            redirect={steps[selected].redirect}
                                            label={steps[selected].btnLabel}
                                            classname={styles.o_steps__btn}
                                        />
                                    )}
                            </div>
                        </Grow>
                    </div>
                </Fade>
            </section>
        </Fade>
    );
};

export default Steps;
